import { globalErrorCondition } from "../../../Helpers/ErrorHelper";
import { BOOKINGS_API_URL } from "../Bookings";
import { parseEntityWithCustomForm } from "../../../Helpers/FormHelper";
import {
    bookingFormCategoriesLoaded,
    bookingFormCategoryLoaded,
    editBookingLoaded,
    restoreBookingLoaded
} from "../Redux/BookingFormSlice";
import { bookingConfigLoadedForForm, bookingConfigRequested } from "../Redux/BookingGlobalActions";
import { startOfDay } from "date-fns";
import { dateFromState, dateToState } from "../../../Helpers/DateTimeHelper";


/**
 * Retrieve the full data for all categories and update redux state
 * @param unitId
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const getCategoriesData = (unitId) => async (dispatch, getState) => {
    let params = {
        unit_id: unitId
    };

    try {
        const result = await axios.get(`${BOOKINGS_API_URL}categories`, { params: params });
        dispatch( bookingFormCategoriesLoaded(result.data.categories) );
    } catch (error) {
        globalErrorCondition('Error loading booking category summaries data', error);
    } finally {
    }
}

export const getCategoryAndAmenitiesData = (categorySlug, unitId, date, tags) => async (dispatch, getState) => {
    try {
        let params = {
            ...(unitId && {unit_id: unitId}),
            ...(date && {date: date}),
            ...(tags && {tags: tags}),
        };

        const result = await axios.get(`${BOOKINGS_API_URL}categories/${categorySlug}`, { params: params });

        dispatch( bookingFormCategoryLoaded(result.data.category, result.data.amenities) );

    } catch (error) {
        globalErrorCondition('Error loading booking category data', error);
    } finally {
    }
}


/**
 *
 * @param categorySlug
 * @param amenitySlug
 * @param date
 * @param boundary
 * @param unitId
 * @param editingThisBookingId
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const getAmenityData = (
    categorySlug,
    amenitySlug,
    date = null,
    boundary = null,
    unitId = null,
    editingThisBookingId = null
) => async (dispatch, getState) => {
    try {
        // TODO - encapsulate out common code when I figure out how to do it
        dispatch( bookingConfigRequested() );

        if (!boundary) {
            boundary = {
                'start': startOfDay(dateFromState(date)),
                'end': null, // Leaving this null will cause the server to return two days of availability
            };
        }

        const result = await axios.get(`${BOOKINGS_API_URL}categories/${categorySlug}/${amenitySlug}`, {
            params: {
                date: date,
                boundaryStart: dateToState(boundary.start), // Ensure we convert to a string without timezone
                boundaryEnd: dateToState(boundary.end),
                unitId: unitId,
                editingThisBookingId: editingThisBookingId
            }
        });

        dispatch( bookingConfigLoadedForForm(result.data.amenity, result.data.category, result.data.config) );

    } catch (error) {
        globalErrorCondition('Error loading booking config data', error);
    } finally {
    }
}

export const getEditBookingData = (editBookingId) => async (dispatch, getState) => {
    try {
        const response = await axios.get(`${BOOKINGS_API_URL}booking/${editBookingId}/edit`, { params: {} });
        const data = response.data.data;

        dispatch(
            editBookingLoaded(
                parseEntityWithCustomForm(data.booking),
                data.amenity,
                data.category,
                data.bookingConfig
            )
        )
    } catch (error) {
        globalErrorCondition('Error loading booking for edit', error);
    } finally {
    }
}

export const getRestoreBookingData = (restoreBookingId, navigate) => async (dispatch, getState) => {
    try {
        const response = await axios.get(`${BOOKINGS_API_URL}booking/${restoreBookingId}/restore`, { params: {} });
        const data = response.data.data;

        dispatch(
            restoreBookingLoaded(
                parseEntityWithCustomForm(data.booking),
                data.amenity,
                data.category,
                data.bookingConfig
            )
        )

        navigate(`/bookings/book/${data.category.slug}/list/${data.amenity.slug}`);

    } catch (error) {
        globalErrorCondition('Error loading booking for restore', error);
    } finally {
    }
}

export const checkUnitValidity = async(unitId, categoryId, amenityId) => {
    const requestData = {
        unit_id: unitId,
        category_id: categoryId,
        amenity_id: amenityId,
    }
    const response = await axios.post(`${BOOKINGS_API_URL}config/check-validity-for-unit`, requestData);
    const data = response.data.data;

    return data.is_valid;
}

export const checkAvailability = async(interval, bookingConfig, editingThisBookingId = null) => {
    if (!interval || !interval.start) {
        return null;
    }

    const params = {
        date: interval.start,
        categoryId: bookingConfig.category_id,
        amenityId: bookingConfig.amenity_id,
        unitId: bookingConfig.user_id,
        start: interval.start,
        end: interval.end,
        editingThisBookingId: editingThisBookingId
    };

    try {
        const response = await axios.post(`${BOOKINGS_API_URL}availability/check`, params)
        return response.data.data;
    } catch (error) {
        return null;
    }
}
